import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShield,
  faStar,
  faUserCheck,
  faUserXmark,
  faPlus,
  faTrash,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

import { Button, Spinner } from '@/components/ui';
import { Select } from '@/components/forms';
import { useModalStore, useAuthStore } from '@/stores';
import { useUserDetail, useUpdateUser } from '@/api/useUsers';
import { useAddProviderUser } from '@/api/useProviderUsers';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';

import styles from './UserDetailModal.module.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ProviderSearchResult {
  id: string;
  nombre_comercial: string;
  state: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'provider', label: 'Provider' },
];

const PROVIDER_ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'editor', label: 'Editor' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getEffectiveRoleLabel(user: {
  is_superuser: boolean;
  is_staff: boolean;
  role: string;
}): string {
  if (user.is_superuser) return 'Admin';
  if (user.is_staff) return 'Staff';
  if (user.role === 'provider') return 'Provider';
  return 'Unassigned';
}

// ─── Inline Confirmation Button ──────────────────────────────────────────────

interface ConfirmButtonProps {
  label: string;
  confirmLabel?: string;
  variant: 'primary' | 'secondary' | 'danger' | 'warning' | 'info' | 'success';
  confirmVariant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'info' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  onConfirm: () => void;
  icon?: React.ReactNode;
}

function ConfirmButton({
  label,
  confirmLabel = 'Confirm?',
  variant,
  confirmVariant = 'danger',
  size = 'sm',
  disabled = false,
  onConfirm,
  icon,
}: ConfirmButtonProps) {
  const [pending, setPending] = useState(false);

  const handleClick = () => {
    if (!pending) {
      setPending(true);
      return;
    }
    setPending(false);
    onConfirm();
  };

  // Reset if disabled changes (e.g. mutation in-flight)
  useEffect(() => {
    if (disabled) setPending(false);
  }, [disabled]);

  return (
    <div className={styles.confirmButtonWrapper}>
      <Button
        variant={pending ? confirmVariant : variant}
        size={size}
        disabled={disabled}
        onClick={handleClick}
      >
        {icon && !pending && icon}
        {pending ? confirmLabel : label}
      </Button>
      {pending && (
        <Button
          variant="secondary"
          size={size}
          disabled={disabled}
          onClick={() => setPending(false)}
        >
          Cancel
        </Button>
      )}
    </div>
  );
}

// ─── Provider Assignment Section ─────────────────────────────────────────────

interface ProviderAssignmentSectionProps {
  userId: string;
  userEmail: string;
  assignments: Array<{
    id: string;
    provider: string;
    nombre_comercial: string;
    provider_role: 'owner' | 'editor';
  }>;
  onAssignmentChange: () => void;
}

function ProviderAssignmentSection({
  userId,
  userEmail,
  assignments,
  onAssignmentChange,
}: ProviderAssignmentSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProviderSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [selectedProviderName, setSelectedProviderName] = useState('');
  const [providerRole, setProviderRole] = useState<'owner' | 'editor'>('editor');
  const [removingId, setRemovingId] = useState<string | null>(null);

  // We need the provider id for the add/remove hooks, but provider id changes per action.
  // We'll call the API directly for add (using the selected provider id) and
  // use a local remove handler.
  const addProviderUser = useAddProviderUser(selectedProviderId);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const { data } = await api.get<
          { results: ProviderSearchResult[] } | ProviderSearchResult[]
        >(`${API_ROUTES.providers}?search=${encodeURIComponent(searchQuery)}&page_size=10`);
        const list = Array.isArray(data) ? data : (data.results ?? []);
        setSearchResults(list);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectProvider = (provider: ProviderSearchResult) => {
    setSelectedProviderId(String(provider.id));
    setSelectedProviderName(provider.nombre_comercial);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleAdd = () => {
    if (!selectedProviderId) {
      toast.error('Select a provider first');
      return;
    }
    addProviderUser.mutate(
      { email: userEmail, provider_role: providerRole },
      {
        onSuccess: () => {
          toast.success('Provider assigned');
          setSelectedProviderId('');
          setSelectedProviderName('');
          setProviderRole('editor');
          onAssignmentChange();
        },
        onError: () => toast.error('Failed to assign provider'),
      },
    );
  };

  const handleRemove = async (providerUserId: string, providerId: string) => {
    setRemovingId(providerUserId);
    try {
      await api.delete(`/api/providers/providers/${providerId}/users/${providerUserId}/`);
      toast.success('Provider removed');
      onAssignmentChange();
    } catch {
      toast.error('Failed to remove provider');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className={styles.section}>
      <h4 className={styles.sectionTitle}>
        <FontAwesomeIcon icon={faBuilding} className={styles.sectionIcon} />
        Provider Assignments
      </h4>

      {/* Assigned providers list */}
      {assignments.length === 0 ? (
        <p className={styles.emptyAssignments}>No providers assigned.</p>
      ) : (
        <ul className={styles.providerList}>
          {assignments.map((a) => (
            <li key={a.id} className={styles.providerItem}>
              <div className={styles.providerItemInfo}>
                <span className={styles.providerName}>{a.nombre_comercial}</span>
                <span
                  className={`${styles.roleBadge} ${
                    a.provider_role === 'owner' ? styles.roleOwner : styles.roleEditor
                  }`}
                >
                  {a.provider_role}
                </span>
              </div>
              <ConfirmButton
                label="Remove"
                confirmLabel="Sure?"
                variant="secondary"
                confirmVariant="danger"
                size="sm"
                disabled={removingId === a.id}
                onConfirm={() => handleRemove(a.id, a.provider)}
                icon={<FontAwesomeIcon icon={faTrash} />}
              />
            </li>
          ))}
        </ul>
      )}

      {/* Add provider form */}
      <div className={styles.addProviderForm}>
        <div className={styles.providerSearchWrapper}>
          <input
            className={styles.providerSearchInput}
            type="text"
            placeholder="Search providers by name..."
            value={selectedProviderId ? selectedProviderName : searchQuery}
            onChange={(e) => {
              setSelectedProviderId('');
              setSelectedProviderName('');
              setSearchQuery(e.target.value);
            }}
            onFocus={() => {
              if (selectedProviderId) {
                setSelectedProviderId('');
                setSelectedProviderName('');
              }
            }}
          />
          {isSearching && <span className={styles.searchingHint}>Searching...</span>}
          {searchResults.length > 0 && !selectedProviderId && (
            <ul className={styles.searchDropdown}>
              {searchResults.map((p) => (
                <li
                  key={p.id}
                  className={styles.searchDropdownItem}
                  onMouseDown={() => handleSelectProvider(p)}
                >
                  {p.nombre_comercial}
                  <span className={styles.providerState}>{p.state}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.addProviderRole}>
          <Select
            value={providerRole}
            onChange={(e) => setProviderRole(e.target.value as 'owner' | 'editor')}
            options={PROVIDER_ROLE_OPTIONS}
            size="sm"
          />
        </div>

        <Button
          variant="primary"
          size="sm"
          disabled={!selectedProviderId || addProviderUser.isPending}
          onClick={handleAdd}
        >
          <FontAwesomeIcon icon={faPlus} />
          {addProviderUser.isPending ? 'Adding...' : 'Add'}
        </Button>
      </div>
      {/* Suppress unused variable warning for userId (needed for context) */}
      <span data-user-id={userId} style={{ display: 'none' }} />
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

interface UserDetailModalProps {
  userId: string;
  handleClose?: () => void;
}

export function UserDetailModal({ userId, handleClose }: UserDetailModalProps) {
  const { closeModal } = useModalStore();
  const { isAdmin } = useAuthStore();
  const currentUserIsAdmin = isAdmin();

  const { data: user, isLoading, isError, refetch } = useUserDetail(userId);
  const updateUser = useUpdateUser();

  const handleDismiss = () => {
    if (handleClose) handleClose();
    else closeModal();
  };

  const handleUpdate = (
    payload: Parameters<typeof updateUser.mutate>[0],
    successMsg: string,
    errorMsg: string,
  ) => {
    updateUser.mutate(payload, {
      onSuccess: () => toast.success(successMsg),
      onError: () => toast.error(errorMsg),
    });
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className={styles.container}>
        <p className={styles.errorState}>Failed to load user details.</p>
        <Button variant="secondary" size="sm" onClick={handleDismiss}>
          Close
        </Button>
      </div>
    );
  }

  const effectiveRole = getEffectiveRoleLabel(user);
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || '—';
  const isPending = updateUser.isPending;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>{fullName}</h3>
          <p className={styles.email}>{user.email}</p>
        </div>
        <span
          className={`${styles.effectiveBadge} ${
            effectiveRole === 'Admin'
              ? styles.badgeAdmin
              : effectiveRole === 'Staff'
                ? styles.badgeStaff
                : effectiveRole === 'Provider'
                  ? styles.badgeProvider
                  : styles.badgeUnassigned
          }`}
        >
          {effectiveRole}
        </span>
      </div>

      {/* Meta row */}
      <div className={styles.metaRow}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Joined</span>
          <span className={styles.metaValue}>{formatDate(user.date_joined)}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Last Login</span>
          <span className={styles.metaValue}>{formatDate(user.last_login)}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Country</span>
          <span className={styles.metaValue}>{user.country || '—'}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Status</span>
          <span
            className={`${styles.statusPill} ${
              user.is_active ? styles.pillActive : styles.pillInactive
            }`}
          >
            {user.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      <div className={styles.divider} />

      {/* Role Management */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          <FontAwesomeIcon icon={faStar} className={styles.sectionIcon} />
          Role Management
        </h4>

        <div className={styles.roleGrid}>
          {/* profile.role */}
          <div className={styles.roleRow}>
            <span className={styles.roleLabel}>Profile Role</span>
            <Select
              value={user.role}
              onChange={(e) =>
                handleUpdate(
                  { id: userId, role: e.target.value as 'unassigned' | 'provider' },
                  `Role updated to ${e.target.value}`,
                  'Failed to update role',
                )
              }
              options={ROLE_OPTIONS}
              size="sm"
              disabled={isPending}
            />
          </div>

          {/* Staff toggle */}
          <div className={styles.roleRow}>
            <span className={styles.roleLabel}>
              Staff Access
              <span
                className={`${styles.currentBadge} ${user.is_staff ? styles.currentOn : styles.currentOff}`}
              >
                {user.is_staff ? 'ON' : 'OFF'}
              </span>
            </span>
            <ConfirmButton
              label={user.is_staff ? 'Remove Staff' : 'Grant Staff'}
              confirmLabel={user.is_staff ? 'Remove?' : 'Grant?'}
              variant={user.is_staff ? 'warning' : 'secondary'}
              confirmVariant={user.is_staff ? 'danger' : 'success'}
              size="sm"
              disabled={isPending}
              icon={<FontAwesomeIcon icon={faStar} />}
              onConfirm={() =>
                handleUpdate(
                  { id: userId, is_staff: !user.is_staff },
                  user.is_staff ? 'Staff access removed' : 'Staff access granted',
                  'Failed to update staff status',
                )
              }
            />
          </div>

          {/* Admin toggle — admin only */}
          {currentUserIsAdmin && (
            <div className={styles.roleRow}>
              <span className={styles.roleLabel}>
                Admin Access
                <span
                  className={`${styles.currentBadge} ${user.is_superuser ? styles.currentOn : styles.currentOff}`}
                >
                  {user.is_superuser ? 'ON' : 'OFF'}
                </span>
              </span>
              <ConfirmButton
                label={user.is_superuser ? 'Remove Admin' : 'Grant Admin'}
                confirmLabel={user.is_superuser ? 'Remove?' : 'Grant?'}
                variant={user.is_superuser ? 'info' : 'secondary'}
                confirmVariant={user.is_superuser ? 'danger' : 'info'}
                size="sm"
                disabled={isPending}
                icon={<FontAwesomeIcon icon={faShield} />}
                onConfirm={() =>
                  handleUpdate(
                    { id: userId, is_superuser: !user.is_superuser },
                    user.is_superuser ? 'Admin access removed' : 'Admin access granted',
                    'Failed to update admin status',
                  )
                }
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      {/* Status */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>
          <FontAwesomeIcon
            icon={user.is_active ? faUserCheck : faUserXmark}
            className={styles.sectionIcon}
          />
          Account Status
        </h4>
        <div className={styles.statusRow}>
          <p className={styles.statusDescription}>
            {user.is_active
              ? 'This account is active and can log in.'
              : 'This account is deactivated and cannot log in.'}
          </p>
          <ConfirmButton
            label={user.is_active ? 'Deactivate' : 'Activate'}
            confirmLabel={user.is_active ? 'Deactivate?' : 'Activate?'}
            variant={user.is_active ? 'danger' : 'success'}
            confirmVariant={user.is_active ? 'danger' : 'success'}
            size="sm"
            disabled={isPending}
            icon={<FontAwesomeIcon icon={user.is_active ? faUserXmark : faUserCheck} />}
            onConfirm={() =>
              handleUpdate(
                { id: userId, is_active: !user.is_active },
                user.is_active ? 'User deactivated' : 'User activated',
                'Failed to update user status',
              )
            }
          />
        </div>
      </div>

      <div className={styles.divider} />

      {/* Provider Assignments */}
      <ProviderAssignmentSection
        userId={userId}
        userEmail={user.email}
        assignments={user.providers ?? []}
        onAssignmentChange={() => refetch()}
      />

      {/* Footer */}
      <div className={styles.footer}>
        <Button variant="secondary" size="sm" onClick={handleDismiss}>
          Close
        </Button>
      </div>
    </div>
  );
}
